import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  clearPunctualityDay,
  getPunctualityNotices,
  sendPunctualityDay,
  uploadPunctualityDirectory
} from 'api/kekaAttendance'

// Centre Audit > Punctuality Mails (2 Nov 2026). One day of Keka at a time:
// who was late or absent, their nth late day this month and total deduction.
// Remove anyone wrong, then send. Rules live in the API.

const POLL_MS = 5000
const POLL_TIMES = 36

const STATUS_LABEL = {
  to_send: 'to send',
  sending: 'sending',
  sent: 'sent',
  failed: 'failed',
  cleared: 'removed'
}

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2), width: '100%' },
  bar: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  note: { marginBottom: theme.spacing(2) },
  error: { color: theme.palette.error.main, marginBottom: theme.spacing(2) },
  faded: { opacity: 0.5 }
}))

const canSend = (row) =>
  row.email && (row.status === 'to_send' || row.status === 'failed')

const errorText = (err) =>
  (err && err.response && err.response.data && err.response.data.message) ||
  (err && err.message) ||
  'Something went wrong.'

const PunctualityMails = () => {
  const classes = useStyles()
  const [date, setDate] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [confirming, setConfirming] = useState(false)
  const poll = useRef(null)

  const load = useCallback(async (day) => {
    try {
      const result = await getPunctualityNotices(day)
      setData(result)
      setDate(result.date || '')
      setError('')
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    return () => clearInterval(poll.current)
  }, [load])

  const act = async (work) => {
    setError('')
    setMessage('')
    try {
      await work()
    } catch (err) {
      setError(errorText(err))
    }
  }

  const send = () =>
    act(async () => {
      setConfirming(false)
      await sendPunctualityDay(date)
      setMessage('Sending. This list updates as each mail goes.')
      clearInterval(poll.current)
      let left = POLL_TIMES
      poll.current = setInterval(() => {
        left -= 1
        if (left <= 0) clearInterval(poll.current)
        load(date)
      }, POLL_MS)
    })

  const toggleRemoved = (row) =>
    act(async () => {
      const result = await clearPunctualityDay({
        date,
        no: row.no,
        undo: row.status === 'cleared'
      })
      if (result.note) setMessage(`Removed. ${result.note}`)
      await load(date)
    })

  const upload = (event) => {
    const file = event.target.files[0]
    event.target.value = ''
    if (!file) return
    act(async () => {
      const result = await uploadPunctualityDirectory(file)
      setMessage(
        `Emails saved for ${result.updated} people. Not in Keka attendance: ${result.unknown}. No email in file: ${result.no_email}.`
      )
      await load(date)
    })
  }

  const rows = (data && data.rows) || []
  const ready = rows.filter(canSend)
  const missing = rows.filter((r) => !r.email && r.status === 'to_send')
  const blocked = data && data.blocked

  return (
    <div className={classes.root}>
      <div className={classes.bar}>
        <TextField
          inputProps={{ max: data ? data.latest_date : undefined }}
          label="Day"
          InputLabelProps={{ shrink: true }}
          onChange={(event) => {
            setDate(event.target.value)
            if (event.target.value) load(event.target.value)
          }}
          type="date"
          value={date}
        />
        <Button
          color="primary"
          disabled={!ready.length || !!blocked}
          onClick={() => setConfirming(true)}
          variant="contained"
        >
          Send {ready.length} mails
        </Button>
        <Button component="label" variant="outlined">
          Upload Keka directory
          <input accept=".xlsx" hidden onChange={upload} type="file" />
        </Button>
      </div>

      {blocked && <Typography className={classes.note}>{blocked}</Typography>}
      {missing.length > 0 && (
        <Typography className={classes.note}>
          {missing.length} people have no email, so they get no mail.
        </Typography>
      )}
      {message && <Typography className={classes.note}>{message}</Typography>}
      {error && <Typography className={classes.error}>{error}</Typography>}

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Centre</TableCell>
                <TableCell>Mark</TableCell>
                <TableCell align="right">Late day</TableCell>
                <TableCell align="right">Total deduction</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Manager cc</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  className={
                    row.status === 'cleared' ? classes.faded : undefined
                  }
                  key={row.no}
                >
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.centre || '-'}</TableCell>
                  <TableCell>
                    {row.kind === 'late'
                      ? `${row.minutes} min late`
                      : 'no show'}
                  </TableCell>
                  <TableCell align="right">{row.late_day}</TableCell>
                  <TableCell align="right">{row.cumulative}</TableCell>
                  <TableCell>{row.email || 'no email'}</TableCell>
                  <TableCell>{row.manager_email || 'none'}</TableCell>
                  <TableCell title={row.error}>
                    {STATUS_LABEL[row.status]}
                  </TableCell>
                  <TableCell>
                    {row.status !== 'sending' && (
                      <Button onClick={() => toggleRemoved(row)} size="small">
                        {row.status === 'cleared' ? 'Put back' : 'Remove'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={9}>
                    Nobody is marked on this day.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog onClose={() => setConfirming(false)} open={confirming}>
        <DialogTitle>Send {ready.length} punctuality mails?</DialogTitle>
        <DialogContent>
          These go to staff and their managers for {date}.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button color="primary" onClick={send} variant="contained">
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default PunctualityMails
